import {
  pageFetchPosters,
  pageGetNextPageHref,
  pageIsThereMorePosters
} from "./page.js";
import {
  mapsGetRegionsForVegueria,
  mapsGetVegueriaForRegion,
  mapsLabel,
  mapsPrefetchVegueries
} from "./maps.js";
import {
  postersEscapeSpacesAndApostrophes,
  postersGetDateString,
  postersGetEndDateOrTime,
  postersReplaceApostrophesInText,
  postersSetExcludedPosterIds,
  postersSetIndexMode
} from "./posters.js";
import { labelsFetchCatalog, labelsGetDisplayLabel, labelsHydratePosterLabels } from "./labels.js";
import { i18nText } from "./i18n.js";
import { loadMoreBind } from "./load-more.js";

function linksGetContentLanguage(poster) {
  return poster.contentLanguage === "es" ? "es" : "ca";
}

function linksLocalizePath(path, language) {
  let normalizedPath = path;
  if (normalizedPath.startsWith("/es/")) {
    normalizedPath = normalizedPath.substring(3);
  } else if (normalizedPath === "/es") {
    normalizedPath = "/";
  } else if (normalizedPath.startsWith("/ca/")) {
    normalizedPath = normalizedPath.substring(3);
  } else if (normalizedPath === "/ca") {
    normalizedPath = "/";
  }
  if (language === "es") {
    if (normalizedPath === "/") {
      return "/es/";
    }
    return "/es" + normalizedPath;
  }
  if (normalizedPath === "/") {
    return "/ca/";
  }
  return "/ca" + normalizedPath;
}

function linksGetRelativeLink(poster) {
  const language = linksGetContentLanguage(poster);
  return linksLocalizePath(
    "/" +
      encodeURIComponent(poster.id) +
      ".html?id=" +
      poster.publisherId,
    language
  );
}

function linksCreateAndAppend(poster, parent) {
  const posterLanguage = linksGetContentLanguage(poster);
  const text = i18nText(posterLanguage);
  let relativeLink = linksGetRelativeLink(poster);

  let template =
    "<div class='posterListBlock'>" +
    "<div class='posterListDate'>" +
    postersGetDateString(poster.dateBegin, posterLanguage) +
    " " +
    postersGetEndDateOrTime(
      poster.dateBegin,
      poster.dateEnd,
      poster.timeBegin,
      posterLanguage
    ) +
    "</div>" +
    "<div class='posterListTitle'>" +
    "<a href='" +
    relativeLink +
    "'>" +
    poster.title +
    "</a>" +
    "</div>";

  if (poster.subtitle !== "") {
    template += "<div class='posterListSubtitle'>" + poster.subtitle + "</div>";
  }
  template += "</div>"; // posterListBlock
  template += "<div class='posterLabelWrapper'>";
  const posterIdParam = encodeURIComponent(poster.id);
  const posterParentParam = encodeURIComponent(poster.publisherId);
  poster.labelsList.forEach((value) => {
    template +=
      "<a class='posterLabel' href='" +
      linksLocalizePath(mapsLabel(poster.region, value), posterLanguage) +
      "' data-label-value='" +
      postersReplaceApostrophesInText(value) +
      "' data-label-language='" +
      posterLanguage +
      "'>" +
      labelsGetDisplayLabel(value, posterLanguage) +
      "</a>";
  });
  template +=
    "<a class='posterLabel' href='" +
    linksLocalizePath("/map.html", posterLanguage) +
    "?lat=" +
    poster.latitude +
    "&lng=" +
    poster.longitude +
    "&zoom=15&posterId=" +
    posterIdParam +
    "&posterParent=" +
    posterParentParam +
    "'>" +
    poster.town +
    "</a>";

  template +=
    "<a class='posterLabel' href='" +
    linksLocalizePath(
      "/" + postersEscapeSpacesAndApostrophes(poster.region),
      posterLanguage
    ) +
    "'>" +
    poster.region +
    "</a>";

  template += "</div>"; // posterLabelWrapper

  template +=
    "<div id='posterSocial'>" +
    "<div style='margin-bottom: 1em;'>" +
    text.shareThanks +
    "</div>" +
    "<div class='fb-like' data-width='' data-layout='button_count' data-action='like' data-size='large' data-share='true' data-href='https://cartells.yuppiya.com" +
    relativeLink +
    "'></div></div>";

  let wrapper = document.createElement("div");
  wrapper.innerHTML = template;
  wrapper.setAttribute("class", "posterListWrapper");
  wrapper.setAttribute("lang", linksGetContentLanguage(poster));
  labelsHydratePosterLabels(wrapper);

  parent.appendChild(wrapper);

  return wrapper;
}

function linksGetMainPoster() {
  return document.getElementById("poster-main-poster");
}

function linksGetMainPosterRegion() {
  const mainPoster = linksGetMainPoster();
  if (!mainPoster) {
    return "";
  }

  const regionFromData = mainPoster.getAttribute("data-post-region");
  if (regionFromData) {
    return regionFromData.trim();
  }

  const regionAnchors = mainPoster.querySelectorAll(".posterRegionWrapper .posterLabel");
  if (regionAnchors.length > 0) {
    return regionAnchors[regionAnchors.length - 1].textContent.trim();
  }

  return "";
}

function linksGetOtherVegueria() {
  const region = linksGetMainPosterRegion();
  if (region === "") {
    return "";
  }
  return mapsGetVegueriaForRegion(region);
}

function linksSetOtherVegueriaTitle(vegueria) {
  const listing = document.getElementById("posterLinksWrapper");
  if (!listing) {
    return;
  }

  let filters = document.getElementById("posterOtherFilters");
  if (vegueria === "") {
    if (filters) {
      filters.remove();
    }
    return;
  }

  if (!filters) {
    filters = document.createElement("div");
    filters.setAttribute("id", "posterOtherFilters");

    const filter = document.createElement("div");
    filter.setAttribute("id", "posterOtherFilterVegueria");

    const subtitle = document.createElement("h2");
    subtitle.setAttribute("id", "posterOtherVegueriaTitle");
    subtitle.setAttribute("class", "mainFilterText");

    const closeButton = document.createElement("a");
    closeButton.setAttribute("id", "posterOtherFilterAnchor");
    closeButton.setAttribute("class", "mainFilterAnchor");
    closeButton.setAttribute("href", linksLocalizePath("/", document.documentElement.lang === "es" ? "es" : "ca"));
    closeButton.textContent = "\u2a2f";

    filter.appendChild(subtitle);
    filter.appendChild(closeButton);
    filters.appendChild(filter);
    listing.insertAdjacentElement("beforebegin", filters);
  }

  document.getElementById("posterOtherVegueriaTitle").textContent = vegueria;
}

function linksParsePosterDate(value) {
  const parts = typeof value === "string" ? value.split("/") : [];
  if (parts.length !== 3) {
    return null;
  }
  const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function linksTemporalSkipPoster(params) {
  const requested = params.get("from") || document.getElementById("listingDateFrom")?.value;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(requested || "")) {
    return null;
  }
  const parts = requested.split("-");
  const threshold = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return (poster) => {
    const end = linksParsePosterDate(poster.dateEnd) || linksParsePosterDate(poster.dateBegin);
    return end !== null && end.getTime() < threshold.getTime();
  };
}

function linksCreate(parentId) {
  let parent = document.getElementById(parentId);

  let posterWrapper = document.getElementsByClassName("posterWrapper")[0];
  if (posterWrapper) {
    const posterId = posterWrapper.getAttribute("data-post-id");
    postersSetExcludedPosterIds(posterId ? [posterId] : []);
  }
  postersSetIndexMode("others");
  const params = new URL(document.location).searchParams;
  let otherIndex = parseInt(params.get("otherIndex"), 10);
  if (Number.isNaN(otherIndex)) {
    otherIndex = 0;
  }
  const otherCursor = params.get("otherCursor") || "";
  const temporalSkipPoster = linksTemporalSkipPoster(params);

  mapsPrefetchVegueries().finally(() => {
    const vegueria = linksGetOtherVegueria();
    const regions = vegueria === "" ? "" : mapsGetRegionsForVegueria(vegueria);
    linksSetOtherVegueriaTitle(vegueria);

    pageFetchPosters(parent, regions, "", "", otherIndex, otherCursor, null, {
      skipPoster: temporalSkipPoster
    }).then(() => {
      const nextButtonWrapper = document.getElementById(
        "posterNextPageButtonWrapper"
      );
      const nextButton = document.getElementById("posterNextPageButton");

      if (pageIsThereMorePosters() === true) {
        nextButtonWrapper.style.display = "block";
        nextButton.setAttribute(
          "href",
          pageGetNextPageHref("otherIndex", true, "otherCursor")
        );
        loadMoreBind(nextButton, async () => {
          const href = nextButton.getAttribute("href");
          if (!href) {
            return;
          }
          const nextUrl = new URL(href, window.location.origin);
          const nextIndex = parseInt(nextUrl.searchParams.get("otherIndex"), 10);
          const nextCursor = nextUrl.searchParams.get("otherCursor") || "";
          postersSetIndexMode("others");
          await pageFetchPosters(
            parent,
            regions,
            "",
            "",
            Number.isNaN(nextIndex) ? 0 : nextIndex,
            nextCursor,
            null,
            {skipPoster: temporalSkipPoster}
          );
          if (pageIsThereMorePosters()) {
            nextButton.setAttribute(
              "href",
              pageGetNextPageHref("otherIndex", true, "otherCursor")
            );
          } else {
            nextButtonWrapper.style.display = "none";
            nextButton.removeAttribute("href");
          }
        });
      } else {
        nextButtonWrapper.style.display = "none";
        nextButton.removeAttribute("href");
      }
    });
  });
}

export { linksCreate, linksCreateAndAppend };

labelsFetchCatalog();
